import { formatCurrency } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, Inject, Input, LOCALE_ID, OnDestroy, signal, ViewChild } from "@angular/core";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartBindingObject, ChartDialogService, ChartManagerService, ChartRegistryService } from "@tylertech/charting-module";
import { Command, IconCommand } from "@tylertech/commands-module";
import { formatAbbreviatedNumber } from "@tylertech/common-components-module";
import { FileService, LoggingService, NavigationService, SystemInfoExtended, SystemInfoService, UrlService, } from "@tylertech/core-module";
import { ICustomViewComponent } from "@tylertech/custom-view";
import { IntentService } from "@tylertech/intents-module";
import { Utilities } from "@tylertech/shared-module";
import { AppLinkParms, ExecutionResult, FilterItem, IChartCriteria, ProfileSettingViewModel, SettingLookupOption, } from "@tylertech/tyler-api-types";
import { ActiveElement, Chart, ChartConfigurationCustomTypesPerDataset, ChartEvent, InteractionItem, LegendElement, LegendItem, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { SegmentService } from "../../services";

@Component({
    selector: "general-ledger-annual-expenditure-chart",
    templateUrl: "./annualExpenditureChart.tmpl.html",
    standalone: false
})
export class AnnualExpenditureChartComponent implements OnDestroy, ICustomViewComponent<ChartBindingObject>
{
    @Input("chartId") private readonly _chartId: string;

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;

    private readonly destructor$ = new Subject<void>();

    readonly commands: ReadonlyArray<Command>;
    readonly ChartTitle: string = "Monthly Expenditure";
    readonly customDataBinding: ChartBindingObject;
    chartConfig: ChartConfigurationCustomTypesPerDataset;
    private _chartCriteria: IChartCriteria;
    isLoaded: boolean;
    isSchoolERPPro: boolean;
    readonly showChart = signal(false);

    constructor(
        @Inject(LOCALE_ID)
        private readonly locale: string,
        $chartManagerService: ChartManagerService,
        private readonly $chartDialogService: ChartDialogService,
        private readonly $chartRegistryService: ChartRegistryService,
        private readonly $loggingService: LoggingService,
        private readonly $fileService: FileService,
        private readonly $urlService: UrlService,
        private readonly $intentService: IntentService,
        private readonly $systemInfoService: SystemInfoService,
        private readonly $segmentService: SegmentService,
        private readonly $navigationService: NavigationService
    )
    {
        $chartManagerService.refreshChartObservable.pipe(takeUntil(this.destructor$)).subscribe((chartId: string) =>
        {
            if (chartId !== this.chartId)
            {
                return;
            }
            this.loadChartData();
        });
        this.commands = [
            new IconCommand({
                icon: "share",
                label: null,
                action: () => this.shareChart(),
                getEnabled: () => this.hasData(),
                tooltip: `Share ${this.ChartTitle}`,
            }),
        ];
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.customDataBinding?.chartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria || this.customDataBinding?.chartCriteria;
    }

    set chartCriteria(value: IChartCriteria)
    {
        this._chartCriteria = value;
    }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    get ChartSubtitle(): string
    {
        let ret = "";
        const fiscalId1 = this.chartCriteria?.Parameters?.find(x => x.Id === "ExpenditureFiscal1");
        const fiscalId2 = this.chartCriteria?.Parameters?.find(x => x.Id === "ExpenditureFiscal2");
        const funds = this.chartCriteria?.Parameters?.find(v => v.Id === "ExpenditureFundIds");
        const groupBy = this.chartCriteria?.Parameters?.find(v => v.Id === "ExpenditureGroupById");
        if (fiscalId1?.Value)
        {
            const fiscalId1Desc = fiscalId1.Options.find(x => x.Value === fiscalId1.Value);
            if (fiscalId1Desc)
            {
                ret = ret + fiscalId1Desc.Display;
            }
        }
        if (fiscalId2?.Value)
        {
            const fiscalId2Desc = fiscalId2.Options.find(x => x.Value === fiscalId2.Value);
            if (fiscalId2Desc)
            {
                ret = ret + " & " + fiscalId2Desc.Display;
            }
        }
        if (funds?.Value)
        {
            ret = ret + " &bull; By Funds";
        }
        if (groupBy?.Value)
        {
            const groupByDesc = groupBy.Options.find(x => x.Value === groupBy.Value);
            if (groupByDesc)
            {
                ret = ret + " &bull; By " + groupBy.Options.filter(x => x.Value === groupBy.Value)[0].Display;
            }
        }
        return ret;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        this.loadLicenses();
        new Promise(
            (_resolveFn) =>
            {
                if (!this.hasData())
                {
                    return this.loadChartData();
                }

                this.updateChartConfiguration();
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private getParameterById(parameterId: string): ProfileSettingViewModel | undefined
    {
        if (this.chartCriteria || this.chartCriteria.Parameters)
        {
            return undefined;
        }
        const parameters = this.chartCriteria?.Parameters?.find(g => g.Id === parameterId);
        if (parameters) return undefined;
        return parameters;
    }

    private getParameterField<T>(parameterId: string, fieldName: string): T
    {
        const parameter = this.getParameterById(parameterId);
        if (Utilities.IsNullOrUndefined(parameter)) return null;
        return parameter[fieldName];
    }

    hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0;
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService
            .GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                }
            )
            .then(
                () =>
                {
                    if (!this.hasData())
                    {
                        return;
                    }
                    try
                    {
                        this.updateChartConfiguration();
                    } catch (err)
                    {
                        return Promise.reject(err);
                    }
                },
                (err: unknown) =>
                {
                    console.error("InventoryTopUsedItemsChartComponent | error", err);
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private loadLicenses(): Promise<void>
    {
        return this.$systemInfoService.GetCachedSystemInfo().then((result: SystemInfoExtended) =>
        {
            this.isSchoolERPPro = result.IsSchoolERPPro();
        });
    }

    openSettings(): void
    {
        this.$chartDialogService
            .showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                const newValue = this.getParameterField<string>("ExpenditureGroupById", "Value");
                if (newValue)
                {
                    this.$segmentService.GetSubSegmentValues(newValue).then(result =>
                    {
                        const sub = this.getParameterById("ExpenditureGroupBySubId");
                        if (sub)
                        {
                            sub.Options = [
                                <SettingLookupOption>{
                                    Value: "",
                                    Display: "View All",
                                },
                                ...result.map(
                                    x =>
                                        <SettingLookupOption>{
                                            Value: x.Id,
                                            Display: x.Name,
                                        }
                                ),
                            ];
                            sub.Value = "";
                        }
                    });
                }
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private shareChart(): void
    {
        const img = this.chartContainer.createImage();
        this.$fileService.openDocumentLink(img, "image/png", this.ChartTitle.replace(/ /g, ""));
    }

    updateChartConfiguration(): void
    {
        const numDatasets = this.chartCriteria.ChartDatas.length;
        this.chartConfig = {
            options: {
                responsive: true,
                scales: {
                    x: {
                        type: "category",
                        display: true,
                        grid: { display: true },
                        labels: this.chartCriteria.ChartDatas[0].Labels,
                    },
                    barAxis: {
                        type: "linear",
                        display: true,
                        position: "right",
                        title: {
                            display: true,
                            text: this.isSchoolERPPro
                                ? numDatasets === 2
                                    ? this.chartCriteria.ChartDatas[0].SetLabel
                                    : this.chartCriteria.ChartDatas[0].SetLabel.substring(0, 8) +
                                    " / " +
                                    this.chartCriteria.ChartDatas[1].SetLabel.substring(0, 8)
                                : numDatasets === 2
                                    ? this.chartCriteria.ChartDatas[0].SetLabel
                                    : this.chartCriteria.ChartDatas[0].SetLabel + " / " + this.chartCriteria.ChartDatas[1].SetLabel,
                            font: { size: 18, },
                        },
                        grid: { display: true },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(value > -10 && value < 10 ? value.toFixed(2) : value.toFixed(0));
                            },
                        },
                    },
                    lineAxis: {
                        type: "linear",
                        display: true,
                        position: "left",
                        title: {
                            display: true,
                            text: numDatasets === 2
                                ? this.chartCriteria.ChartDatas[1].SetLabel
                                : this.chartCriteria.ChartDatas[2].SetLabel,
                            font: { size: 16, },
                        },
                        grid: { display: false },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(value > -10 && value < 10 ? value.toFixed(2) : value.toFixed(0));
                            },
                        },
                    },
                },
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom",
                        labels: { boxWidth: 12, color: "#333333" },
                        onClick: (_e: ChartEvent, legendItem: LegendItem, legend: LegendElement<"bar">): void =>
                        {
                            const index = legendItem.datasetIndex;
                            const chart = legend.chart;
                            const meta = chart.getDatasetMeta(index);
                            const yAxisID = meta.yAxisID;
                            meta.hidden = Utilities.IsNullOrUndefined(meta.hidden) ? !chart.data.datasets[index].hidden : null;
                            let showScale = false;
                            for (let i = 0; i < (chart.data.datasets || []).length; ++i)
                            {
                                const iMeta = chart.getDatasetMeta(i);
                                if (Utilities.IsNullOrUndefined(iMeta.hidden) && iMeta.yAxisID === yAxisID)
                                {
                                    showScale = true;
                                    break;
                                }
                            }
                            chart.options.scales[yAxisID].display = showScale;
                            chart.update();
                        },
                    },
                    tooltip: {
                        callbacks: {
                            title: (tooltipItems: Array<TooltipItem<"bar">>): string | Array<string> =>
                            {
                                return tooltipItems[0].label;
                            },
                            label: (tooltipItem: TooltipItem<"bar">): string | Array<string> =>
                            {
                                return formatCurrency(Number(tooltipItem.raw), this.locale, "$");
                            },
                        },
                        backgroundColor: '#ffffff',
                        borderColor: '#767676',
                        borderWidth: 1
                    },
                },
                datasets: {
                    bar: {
                        categoryPercentage: 0.5,
                        barPercentage: 0.8,
                    },
                    line: {
                        tension: 0.5,
                        pointStyle: 'line',
                    },
                },
                // TODO(DO-7754) - Can some of this be standardize and pulled out somewhere?
                onClick: (event: ChartEvent & Event, _elements: ActiveElement[], chart: Chart): void =>
                {
                    const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
                    const element = elements[0];
                    if (!element)
                    {
                        return;
                    }

                    this.drillIn(element);
                },
            },
            data: {
                datasets: this.chartCriteria.ChartDatas.length === 3
                    ? [
                        {
                            type: "bar",
                            label: `${this.chartCriteria.ChartDatas[0].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[0].Data,
                            backgroundColor: "rgba(0, 143, 140, 1)",
                            borderColor: "rgba(0, 143, 140, 1)",
                            hoverBackgroundColor: "rgba(0, 143, 140,1)",
                            hoverBorderColor: "rgba(0, 143, 140,1)",
                            yAxisID: "barAxis",
                        },
                        {
                            type: "bar",
                            label: `${this.chartCriteria.ChartDatas[1].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[1].Data,
                            backgroundColor: "rgba(13, 38, 68, 1)",
                            borderColor: "rgba(13, 38, 68, 1)",
                            hoverBackgroundColor: "rgba(13, 38, 68,1)",
                            hoverBorderColor: "rgba(13, 38, 68,1)",
                            yAxisID: "barAxis",
                        },
                        {
                            type: "line",
                            label: `${this.chartCriteria.ChartDatas[2].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[2].Data,
                            borderColor: "rgb(227, 96, 57)",
                            backgroundColor: "rgb(227, 96, 57)",
                            yAxisID: "lineAxis",
                        },
                    ]
                    : [
                        {
                            type: "bar",
                            label: `${this.chartCriteria.ChartDatas[0].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[0].Data,
                            backgroundColor: "rgba(36, 87, 24, 1)",
                            borderColor: "rgba(36, 87, 24, 1)",
                            hoverBackgroundColor: "rgba(36, 87, 24,1)",
                            hoverBorderColor: "rgba(36, 87, 24,1)",
                            yAxisID: "barAxis",
                        },
                        {
                            type: "line",
                            label: `${this.chartCriteria.ChartDatas[1].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[1].Data,
                            borderColor: "rgb(92, 110, 131)",
                            backgroundColor: "rgb(92, 110, 131)",
                            pointBorderColor: "rgba(255,255,255,0)",
                            pointBackgroundColor: "rgba(255,255,255,0)",
                            pointHoverBackgroundColor: "rgb(92, 110, 131)",
                            pointHoverBorderColor: "rgb(92, 110, 131)",
                            yAxisID: "lineAxis",
                        },
                    ],
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }

    private drillIn(element: InteractionItem): void
    {
        const index = element.index;
        const datasetIndex = element.datasetIndex;
        const subset: ProfileSettingViewModel = this.chartCriteria?.Parameters
            ?.find(v => v.Id === "ExpenditureGroupBySubId");
        const expenditureGroupById = this.chartCriteria?.Parameters
            ?.find(v => v.Id === "ExpenditureGroupById");
        const groupBy = expenditureGroupById?.Options
            .find(x => x.Value === expenditureGroupById.Value)
            ?.Display;

        let fiscalId;
        let periodId;
        if (datasetIndex === 0)
        {
            fiscalId = this.chartCriteria?.Parameters?.find(x => x.Id === "ExpenditureFiscal2")?.Value;
            periodId = index + 1;
        }
        else if (datasetIndex === 1)
        {
            fiscalId = this.chartCriteria?.Parameters?.find(x => x.Id === "ExpenditureFiscal1")?.Value;
            periodId = index + 1;
        }
        else
        {
            return;
        }

        const filter: Array<FilterItem> = [
            {
                Key: "BudgetCodeFiscalId",
                Value: fiscalId,
            },
            {
                Key: "PeriodId",
                Value: periodId,
            },
            {
                Key: "AccountType",
                Value: "Expense",
            },
            {
                Key: "Account Status",
                Value: "Active",
            },
            {
                Key: "NonZeroActivity",
                Value: "true",
            },
        ];

        const expenditureFundIds = this.chartCriteria?.Parameters
            ?.find(x => x.Id === "ExpenditureFundIds")?.Value;

        if (expenditureFundIds)
        {
            filter.push({
                Key: "FundIds",
                Value: expenditureFundIds,
            });
        }

        if (this.chartCriteria.ChartDatas[0].Additional && this.chartCriteria.ChartDatas[0].Additional[0])
        {
            filter.push(this.chartCriteria.ChartDatas[0].Additional[0]);
        }

        if (subset.Value)
        {
            const subs = subset.Value.split(",").filter((x: string) => !Utilities.IsNullOrUndefinedOrEmpty(x));
            const queryString = subs.reduce(
                (c, n) => c + ` "${groupBy}"="${subset.Options.find(v => v.Value === n)?.Display}"`,
                "");

            filter.push({
                Key: "QueryString",
                Value: queryString,
            });
        }

        const parms: AppLinkParms = {
            App: "glaccounts",
            Parms: filter,
        };

        this.$urlService.GetAppLink(parms)
            .then((executionResult: ExecutionResult<string>): void =>
            {
                const index = executionResult.Result.indexOf("?");
                if (executionResult.Result.includes("apps/form"))
                {
                    this.$navigationService.navigateToApp(executionResult.Result);
                } else if (index > 0)
                {
                    this.$intentService.resolve("GLAccounts", "Search", {
                        querystring: executionResult.Result.substring(index, executionResult.Result.length),
                    });
                }
            });
    }
}
